/** Additions with duplicate review, separate from existing create/update imports. */
export const ITEM_BULK_RESOURCE = 'item_bulk_upload'

export type RowAction = 'CREATE' | 'UPDATE' | 'UNCHANGED' | 'REJECT' | 'REVIEW' | 'SKIP'

export interface ItemDuplicateMatch {
  readonly source: 'EXISTING' | 'UPLOAD'
  readonly id: string | null
  readonly sheetRow: number | null
  readonly code: string
  readonly name: string
  readonly unit: string
  readonly retired: boolean
  readonly reason: string
  readonly sameCode: boolean
}

export type ItemImportDecision = 'NEW' | 'DUPLICATE' | 'REVIEW'

export interface ItemImportResolution {
  readonly sheetRow: number
  readonly decision: ItemImportDecision
  /** A distinct item needs an unused code when its original code is taken. */
  readonly code?: string
}

export interface ValidatedRow {
  readonly sheetRow: number
  readonly action: RowAction
  readonly id: string | null
  readonly label: string
  readonly values: Record<string, unknown>
  readonly changes: ReadonlyArray<{ field: string; from: string; to: string }>
  readonly problem: string | null
  readonly duplicateMatches?: readonly ItemDuplicateMatch[]
  /**
   * Accepted, but worth a look: a unit not in the Units master, a lookup that
   * is retired, a code that was generated. Never a reason to refuse the row.
   */
  readonly warnings?: readonly string[]
  /** `false` retires the record after it is saved, `true` restores it. */
  readonly setActive?: boolean
  /** An item row with no unit; the job's default unit fills it in. */
  readonly blankUnit?: boolean
  /**
   * Lookups that name a record not there yet (an item category the reference
   * export has and this company does not). Created first when the job says
   * so; otherwise the row is refused.
   */
  readonly pendingLookups?: ReadonlyArray<{ readonly field: string; readonly entity: string; readonly name: string }>
  /** What the row does once its pending lookups are allowed to be created. */
  readonly heldAction?: RowAction
  readonly review?: {
    readonly decision: ItemImportDecision
    readonly reviewedBy: string
    readonly reviewedByName: string
    readonly reviewedAt: string
  }
}

/** A header the wizard read and did not use, and why. */
export interface IgnoredImportHeader {
  readonly header: string
  readonly reason: string
}

/** Names a lookup column used that the target master does not have yet. */
export interface MissingImportLookup {
  readonly field: string
  readonly fieldLabel: string
  readonly entity: string
  readonly entityLabel: string
  readonly names: readonly string[]
  readonly rows: number
  /** Whether a record can be made from the name alone. */
  readonly creatable: boolean
}

/** The operator's answers that apply to the whole file, kept with the job. */
export interface ImportJobOptions {
  /** Written into every item row whose Unit cell was blank. */
  readonly defaultUnit?: string | null
  /** Master keys whose missing records are created before the rows. */
  readonly createMissing?: readonly string[]
}

export interface ImportJobMeta {
  readonly ignoredHeaders: readonly IgnoredImportHeader[]
  readonly missingLookups: readonly MissingImportLookup[]
  /** The Units master's spellings, for the default-unit picker. */
  readonly unitChoices?: readonly string[]
  readonly options: ImportJobOptions
}

export interface ImportJobView {
  readonly id: string
  readonly resource: string
  readonly resourceLabel: string
  readonly fileName: string | null
  readonly state: 'VALIDATED' | 'IMPORTED' | 'FAILED'
  readonly accepted: number
  readonly rejected: number
  readonly createdCount: number
  readonly updatedCount: number
  readonly rows: readonly ValidatedRow[]
  /** Absent on jobs validated before file-wide options existed. */
  readonly meta?: ImportJobMeta
  readonly createdByName: string | null
  readonly createdAt: string
  readonly committedAt: string | null
}
